// import { refreshTokenUser } from '@/api';
import { useAuthStore } from '@/stores/authStore';
import useLanguageStore from '@/stores/useLanguageStore';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_URL, API_VERSION } from './setup.config';
import { useUserStore } from '@/stores';
import { isWorkspaceGone, isStaleSession } from './authErrors';
import { ROUTE } from './routes.config';
import * as Sentry from '@sentry/react';

export const apiConfig = {
  BASE_URL: `${API_URL}/${API_VERSION}`,
};

const setAuthHeader = (config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

const setLangAsParams = (config: InternalAxiosRequestConfig) => {
  const lang = useLanguageStore.getState().language || 'en';
  if (config.params) {
    config.params.lang = lang;
  } else {
    config.params = { lang };
  }
  return config;
};

// refresh token
// const refreshToken = async () => {
//   const { setToken, clearToken } = useAuthStore.getState();
//   const { setUser } = useUserStore.getState();

//   try {
//     const result = await refreshTokenUser();
//     setToken(result?.object?.access_token);
//     return result?.object?.access_token;
//   } catch (error) {
//     clearToken();
//     setUser(undefined);
//     return false;
//   }
// };

const createAxiosInstance = (contentType: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: apiConfig.BASE_URL,
    headers: {
      'Content-Type': contentType,
      'X-Client-Type': 'web',
      //'Document-Policy': 'js-profiling'
    },
  });

  instance.interceptors.request.use(setLangAsParams);

  instance.interceptors.request.use(setAuthHeader);

  // Add Sentry breadcrumb for API requests
  instance.interceptors.request.use((config) => {
    Sentry.addBreadcrumb({
      category: 'api.request',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        params: config.params,
      },
    });
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Add successful response breadcrumb
      Sentry.addBreadcrumb({
        category: 'api.response',
        message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
        level: 'info',
        data: {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
        },
      });
      return response;
    },
    async (error) => {
      const url = error?.config?.url;
      const method = error?.config?.method?.toUpperCase();
      const status = error?.response?.status;

      // Add Sentry context for API errors
      Sentry.addBreadcrumb({
        category: 'api.error',
        message: `${method} ${url} - ${status || 'Network Error'}`,
        level: 'error',
        data: {
          url,
          method,
          status,
          statusText: error?.response?.statusText,
          errorMessage: error?.response?.data?.message,
        },
      });

      // Add context to the error for Sentry
      // Safely parse request data - it might be FormData, object, or JSON string
      let request_data;
      try {
        if (error?.config?.data) {
          // If it's already an object (including FormData), use it as is
          if (typeof error.config.data === 'object') {
            request_data = error.config.data instanceof FormData ? '[FormData]' : error.config.data;
          } else if (typeof error.config.data === 'string') {
            // Only parse if it's a string
            request_data = JSON.parse(error.config.data);
          }
        }
      } catch (parseError) {
        // If parsing fails, just use the raw data as string
        request_data = String(error?.config?.data);
      }

      Sentry.setContext('api_error', {
        endpoint: url,
        method,
        status,
        request_data,
        response_data: error?.response?.data,
        params: error?.config?.params,
      });

      // TODO: skipRedirectUrls for this endpoint was added due to an issue with Zendesk (as Hamid mentioned).
      // ! Once the issue is resolved, this restriction should be removed and redirect should apply to all routes.
      const skipRedirectUrls = ['/merchants/tickets'];

      // const originalRequest = error.config;
      // originalRequest._retryCount = originalRequest._retryCount || 0;

      /*
       * 🔴🔴 SWITCHING WORKSPACE LOGGED THE MERCHANT OUT (QA D9).
       *
       * `POST workspaces/{uid}/activate` REVOKES the previous token by design —
       * that is the security property, a token carries its workspace and must
       * not follow the human. So any request that left the browser carrying the
       * PRE-switch token and answers afterwards gets a 401. Correct on the
       * server; the client then treated it as "your session ended" and threw
       * the merchant onto /login mid-work.
       *
       * QA reproduced it with an in-flight `POST orders/request` delayed six
       * seconds across a switch: the order correctly did not land, and the app
       * went to /login. `cancelQueries()` only covers react-query READS —
       * mutations, uploads, polling and the Sentry/Zendesk calls are not
       * cancelled and cannot all be.
       *
       * ⭐⭐⭐ A 401 FOR A TOKEN WE HAVE ALREADY REPLACED IS NOT A LOGOUT, IT IS
       * A LATE REPLY. Compare the Authorization the request actually went out
       * with against the token the store holds NOW; if they differ, this answer
       * belongs to a session we deliberately ended, and the only correct thing
       * to do with it is drop it.
       */
      const staleSession = isStaleSession(
        (error?.config?.headers?.Authorization ?? '') as string,
        useAuthStore.getState().token,
      );

      if (error?.response?.status === 401 && staleSession) {
        return Promise.reject(error);
      }

      /*
       * The same reasoning for D8's 403. `SetCompanyContext` answers
       * WORKSPACE_FORBIDDEN when the caller is no longer a member of the
       * workspace their token names — which is precisely what a removed member
       * sees. Their token is genuinely dead, so this IS a logout, but it has to
       * be an explained one: the app was rendering an endless spinner with no
       * sidebar and no way out, and a refresh repeated it.
       */
      /*
       * ⚠️ THE CODE LIVES IN `object.error_code`, AND I GUESSED IT WRONG ONCE.
       * (Predicate exported below as `isWorkspaceGone` so the tests exercise
       * this exact code and not a copy of it.)
       *
       * The real body from `SetCompanyContext::forbidden()` is:
       *   { status: 'forbidden', code: 403,
       *     message: 'You do not have access to this workspace.',
       *     object: { error_code: 'WORKSPACE_FORBIDDEN' } }
       *
       * My first predicate tested `typeof (body.code ?? body.message) ===
       * 'string'`. `body.code` is the NUMBER 403, so the condition was false
       * before it ever reached the string match, and the code it was looking
       * for was in `object.error_code` anyway. The screen stayed broken while
       * the fix looked present in review.
       *
       * ⭐⭐⭐ THE DATA IS THE AUTHORITY ON ITS OWN SHAPE — and this shape was
       * written down: the middleware's own comment says "the machine-readable
       * reason travels in object.error_code". Reading the producer beats
       * inferring from the consumer.
       *
       * The `error_code` is the contract; the message is a human sentence that
       * may be translated or reworded, so it is a fallback and never the test.
       */
      const workspaceGone = isWorkspaceGone(error?.response?.status, error?.response?.data);

      if (workspaceGone && !staleSession) {
        const { clearToken } = useAuthStore.getState();
        const { clearUser } = useUserStore.getState();

        try {
          sessionStorage.setItem('auth_notice', 'workspace_access_lost');
        } catch {
          // Private mode / blocked storage: the redirect still matters more
          // than the explanation.
        }

        clearUser();
        clearToken();
        window.location.replace(ROUTE.LOGIN);

        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        const { clearToken } = useAuthStore.getState();
        const { clearUser } = useUserStore.getState();

        const shouldSkip = skipRedirectUrls.some((u) => url?.includes(u));

        if (!shouldSkip) {
          clearUser();
          clearToken();
          window.location.replace(ROUTE.LOGIN);
        }

        // originalRequest._retryCount += 1;
        // try {
        //   const newToken = await refreshToken();
        //   if (newToken) {
        //     originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        //     return instance(originalRequest);
        //   }
        // } catch (tokenError) {
        //   return Promise.reject(tokenError);
        // }
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export const axiosJSONInstance = createAxiosInstance('application/json');
export const axiosMultipartInstance = createAxiosInstance('multipart/form-data');
